import { useState, useEffect, useRef } from 'react';
import { useUser } from '@clerk/clerk-react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import {
  X, Plus, Pencil, Check,
  Globe, MapPin, ArrowLeft,
  Briefcase, Code2, FolderOpen, GraduationCap,
  ChevronRight, AlertCircle, Linkedin, ExternalLink,
  Video, DollarSign, Clock, Sparkles, Loader2, Eye
} from 'lucide-react';
import { useFreelancerProfile } from '@/lib/hooks/use-freelancer-profile';
import { useFreelancerSkills } from '@/lib/hooks/use-freelancer-skills';
import { SUPPORTED_LANGUAGES } from '@/lib/constants/languages';
import { useRole } from '@/hooks/use-role';
import { ProfilePictureUploadGeneric } from '@/components/profile/profile-picture-upload-generic';
import { PortfolioSection } from '@/components/portfolio/portfolio-section';
import { Skeleton } from '@/components/ui/skeleton';
import { useProfileCompletion } from '@/lib/hooks/use-profile-completion';
import { useUnifiedCourses } from '@/lib/hooks/use-unified-courses';
import { usePortfolioProjects } from '@/lib/hooks/use-portfolio-projects';
import { useWorkExperience } from '@/lib/hooks/use-work-experience';
import { WorkExperienceSection } from '@/components/profile/work-experience-section';
import { cn } from '@/lib/utils';
import { emailService } from '@/lib/api/services/email.service';
import { VideoIntroPlayer } from '@/components/profile/video-intro-player';
import { getVideoEmbedInfo } from '@/lib/utils/video';
import { SlideIn, FadeIn } from '@/components/motion';
import { useAIWritingAssistant } from '@/lib/hooks/use-ai-writing-assistant';
import { AIAssistantDialog } from '@/components/ai/ai-assistant-dialog';

export default function GeniusProfile() {
  const navigate = useNavigate();
  const { user } = useUser();
  const { freelancerProfileId } = useRole();
  const {
    freelancerProfile,
    saveFreelancerProfile,
    // isSaving,
    reviewStatus,
    reviewComments,
    submitForReview,
    isSubmittingForReview
  } = useFreelancerProfile(freelancerProfileId, { enableMutations: true });
  const { data: suggestedSkills = [], isLoading: isLoadingSkills } = useFreelancerSkills();

  // Get course progress for completion tracking
  const { courses } = useUnifiedCourses();
  const { projects: portfolioProjects } = usePortfolioProjects(freelancerProfileId || '');
  const { experiences: workExperiences } = useWorkExperience(freelancerProfileId || '');
  const { generateHeadline, generateBio, isLoading: isAIGenerating, generatingType, data: aiGeneratedData, reset: resetAIData } = useAIWritingAssistant();

  // Calculate course progress
  const enrolledCourses = courses?.filter((c) => c.enrollment) || [];
  const completedCourses = enrolledCourses.filter(
    (c) => c.enrollment?.status === "completed"
  ).length;
  const totalCourses = courses?.length || 0;

  const courseProgress = {
    totalCourses,
    completedCourses,
    allCompleted: completedCourses >= totalCourses && totalCourses > 0,
  };

  // Calculate profile completion
  const profileCompletion = useProfileCompletion(
    freelancerProfile,
    courseProgress,
    portfolioProjects?.length || 0,
    workExperiences?.length || 0
  );

  // Form state
  const [displayName, setDisplayName] = useState('');
  const [headline, setHeadline] = useState('');
  const [bio, setBio] = useState('');
  const [linkedinUrl, setLinkedinUrl] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [location, setLocation] = useState('');
  const [isSearchable, setIsSearchable] = useState(true);
  const [skills, setSkills] = useState<string[]>([]);
  const [customSkill, setCustomSkill] = useState('');
  const [languages, setLanguages] = useState<string[]>([]);
  const [availabilityStatus, setAvailabilityStatus] = useState<string>('');
  const [rateRangeMin, setRateRangeMin] = useState<string>('');
  const [rateRangeMax, setRateRangeMax] = useState<string>('');
  const [videoIntroUrls, setVideoIntroUrls] = useState<string[]>([]);
  const [newVideoUrl, setNewVideoUrl] = useState('');
  const [selectedVideoUrl, setSelectedVideoUrl] = useState<string | null>(null);

  // Editing states for sections
  const [isEditingNameHeadline, setIsEditingNameHeadline] = useState(false);
  const [tempDisplayName, setTempDisplayName] = useState('');
  const [tempHeadline, setTempHeadline] = useState('');
  const [isEditingBio, setIsEditingBio] = useState(false);
  const [tempBio, setTempBio] = useState('');
  const [isEditingSkills, setIsEditingSkills] = useState(false);
  const [isEditingLanguages, setIsEditingLanguages] = useState(false);
  const [isEditingDetails, setIsEditingDetails] = useState(false);
  const [isEditingAvailability, setIsEditingAvailability] = useState(false);
  const [isEditingRateRange, setIsEditingRateRange] = useState(false);
  const [isEditingVideoIntros, setIsEditingVideoIntros] = useState(false);
  const [isAIDialogOpen, setIsAIDialogOpen] = useState(false);

  // Track last processed AI data to avoid re-processing
  const lastProcessedAIDataRef = useRef<string | null>(null);

  // Sync local state with freelancer profile data
  useEffect(() => {
    if (freelancerProfile) {
      setDisplayName(freelancerProfile.display_name || '');
      setHeadline(freelancerProfile.headline || '');
      setBio(freelancerProfile.bio || '');
      setLinkedinUrl(freelancerProfile.linkedin_url || '');
      setWebsiteUrl(freelancerProfile.website_url || '');
      setLocation(freelancerProfile.location || '');
      setIsSearchable(freelancerProfile.is_searchable ?? true);
      setSkills(Array.isArray(freelancerProfile.skills) ? (freelancerProfile.skills as string[]) : []);
      setLanguages(Array.isArray(freelancerProfile.languages) ? (freelancerProfile.languages as string[]) : []);
      
      // New fields with type casting
      const profileWithNewFields = freelancerProfile as typeof freelancerProfile & {
        availability_status?: string | null;
        rate_range_min_cents?: number | null;
        rate_range_max_cents?: number | null;
        video_intro_urls?: string[] | null;
        view_count?: number;
      };
      
      setAvailabilityStatus(profileWithNewFields.availability_status || '');
      setRateRangeMin(profileWithNewFields.rate_range_min_cents ? (profileWithNewFields.rate_range_min_cents / 100).toString() : '');
      setRateRangeMax(profileWithNewFields.rate_range_max_cents ? (profileWithNewFields.rate_range_max_cents / 100).toString() : '');
      setVideoIntroUrls(Array.isArray(profileWithNewFields.video_intro_urls) ? profileWithNewFields.video_intro_urls : []);
    }
  }, [freelancerProfile]);

  // Auto-enter edit mode when AI generates text and update temp values
  useEffect(() => {
    if (aiGeneratedData) {
      // Create a unique key for this AI data to track if we've processed it
      const dataKey = `${aiGeneratedData.type}-${aiGeneratedData.text}`;
      
      // Skip if we've already processed this data
      if (lastProcessedAIDataRef.current === dataKey) {
        return;
      }

      if (aiGeneratedData.type === 'headline') {
        // Update tempHeadline with generated text
        if (aiGeneratedData.text) {
          setTempHeadline(aiGeneratedData.text);
        }
        // Auto-enter edit mode if not already editing
        setIsEditingNameHeadline((prev) => {
          if (!prev) {
            // Initialize tempDisplayName if not already set
            setTempDisplayName((prevName) => prevName || displayName);
            return true;
          }
          return prev;
        });
        lastProcessedAIDataRef.current = dataKey;
      } else if (aiGeneratedData.type === 'bio') {
        // Update tempBio with generated text
        if (aiGeneratedData.text) {
          setTempBio(aiGeneratedData.text);
        }
        // Auto-enter edit mode if not already editing
        setIsEditingBio((prev) => {
          if (!prev) {
            return true;
          }
          return prev;
        });
        lastProcessedAIDataRef.current = dataKey;
      }
    }
  }, [aiGeneratedData, displayName]);

  // Send email when profile reaches 100% completion (only once)
  useEffect(() => {
    if (!user?.id || !profileCompletion.isComplete || !freelancerProfile?.email) return;
    
    const storageKey = `genius-profile-completion-email-sent-${user.id}`;
    const hasSentEmail = localStorage.getItem(storageKey) === 'true';
    
    if (!hasSentEmail) {
      // Mark as sent immediately to prevent duplicate sends
      localStorage.setItem(storageKey, 'true');
      
      // Send completion email (non-blocking)
      emailService.sendGeniusProfileCompletedEmail(freelancerProfile.email).catch((error) => {
        console.error('Failed to send profile completion email:', error);
        // Remove the flag on error so we can retry
        localStorage.removeItem(storageKey);
      });
    }
  }, [profileCompletion.isComplete, freelancerProfile?.email, user?.id]);

  const addSkill = (skill: string) => {
    if (!skills.includes(skill) && skills.length < 10) {
      // Always enter edit mode when adding skills so user can continue adding more
      // This ensures the editing interface stays open until user explicitly saves
      setIsEditingSkills(true);
      setSkills(prevSkills => [...prevSkills, skill]);
    }
  };

  const removeSkill = (skill: string) => {
    const newSkills = skills.filter(s => s !== skill);
    setSkills(newSkills);
  };

  const handleAddCustomSkill = () => {
    if (customSkill.trim() && !skills.includes(customSkill.trim())) {
      addSkill(customSkill.trim());
      setCustomSkill('');
    }
  };

  // Save handlers
  const saveNameHeadline = () => {
    setDisplayName(tempDisplayName.trim());
    setHeadline(tempHeadline.trim());
    setIsEditingNameHeadline(false);
    // Reset AI data and ref after saving to prevent re-entering edit mode
    if (aiGeneratedData?.type === 'headline') {
      resetAIData();
      lastProcessedAIDataRef.current = null;
    }
    if (saveFreelancerProfile) {
      saveFreelancerProfile({
        display_name: tempDisplayName.trim(),
        headline: tempHeadline.trim() || null,
      });
    }
  };

  const saveBio = () => {
    setBio(tempBio.trim());
    setIsEditingBio(false);
    // Reset AI data and ref after saving to prevent re-entering edit mode
    if (aiGeneratedData?.type === 'bio') {
      resetAIData();
      lastProcessedAIDataRef.current = null;
    }
    if (saveFreelancerProfile) {
      saveFreelancerProfile({ bio: tempBio.trim() || null });
    }
  };

  const saveSkills = () => {
    setIsEditingSkills(false);
    if (saveFreelancerProfile) {
      saveFreelancerProfile({ skills: skills.length > 0 ? skills : null });
    }
  };

  const saveLanguages = () => {
    setIsEditingLanguages(false);
    if (saveFreelancerProfile) {
      saveFreelancerProfile({ languages: languages.length > 0 ? languages : null });
    }
  };

  const saveDetails = () => {
    setIsEditingDetails(false);
    if (saveFreelancerProfile) {
      saveFreelancerProfile({
        location: location.trim() || null,
        linkedin_url: linkedinUrl.trim() || null,
        website_url: websiteUrl.trim() || null,
      });
    }
  };

  const saveAvailability = () => {
    setIsEditingAvailability(false);
    if (saveFreelancerProfile) {
      saveFreelancerProfile({
        availability_status: availabilityStatus.trim() || null,
      } as Record<string, unknown>);
    }
  };

  const saveRateRange = () => {
    setIsEditingRateRange(false);
    if (saveFreelancerProfile) {
      const minCents = rateRangeMin.trim() ? Math.round(parseFloat(rateRangeMin) * 100) : null;
      const maxCents = rateRangeMax.trim() ? Math.round(parseFloat(rateRangeMax) * 100) : null;
      
      // Validate that min is not greater than max
      if (minCents && maxCents && minCents > maxCents) {
        toast.error('Minimum rate cannot be greater than maximum rate');
        return;
      }
      
      saveFreelancerProfile({
        rate_range_min_cents: minCents,
        rate_range_max_cents: maxCents,
      } as Record<string, unknown>);
    }
  };

  const saveVideoIntros = () => {
    setIsEditingVideoIntros(false);
    if (saveFreelancerProfile) {
      saveFreelancerProfile({
        video_intro_urls: videoIntroUrls.length > 0 ? videoIntroUrls : null,
      } as Record<string, unknown>);
    }
  };

  const handleAddVideoUrl = () => {
    const trimmedUrl = newVideoUrl.trim();
    if (!trimmedUrl) {
      toast.error('Please enter a video URL');
      return;
    }

    // Validate URL format
    const videoInfo = getVideoEmbedInfo(trimmedUrl);
    if (!videoInfo.type) {
      toast.error('Please enter a valid YouTube, Vimeo, or Loom URL');
      return;
    }

    if (videoIntroUrls.includes(trimmedUrl)) {
      toast.error('This video URL is already added');
      return;
    }

    setVideoIntroUrls([...videoIntroUrls, trimmedUrl]);
    setNewVideoUrl('');
  };

  const handleRemoveVideoUrl = (url: string) => {
    setVideoIntroUrls(videoIntroUrls.filter(u => u !== url));
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-8">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 pt-6 sm:pt-8">
        {/* Back Button */}
        <FadeIn>
          <Button
            variant="ghost"
            onClick={() => navigate('/genius/dashboard')}
            className="mb-6 text-slate-600 hover:text-slate-900"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
        </FadeIn>

        {/* Changes Requested Banner */}
        {reviewStatus === 'changes_requested' && reviewComments && (
          <SlideIn direction="down">
            <div className="mb-6 rounded-2xl sm:rounded-3xl lg:rounded-[40px] border-[3px] border-orange-500/60 p-5 sm:p-6 lg:p-8 bg-gradient-to-br from-orange-50/80 to-amber-50/80">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-white rounded-full flex items-center justify-center flex-shrink-0">
                  <AlertCircle className="h-6 w-6 sm:h-8 sm:w-8 text-orange-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="text-xl sm:text-2xl lg:text-[32px] text-slate-900 tracking-tight mb-2">
                    Changes Requested
                  </h2>
                  <p className="text-base sm:text-lg text-slate-700 leading-relaxed whitespace-pre-wrap">
                    {reviewComments}
                  </p>
                </div>
              </div>
            </div>
          </SlideIn>
        )}

        <div className="flex flex-col lg:flex-row gap-6">

          {/* Left Sidebar */}
          <div className="w-full lg:w-[350px] flex-shrink-0 space-y-4 sm:space-y-6">

            {/* Profile Card */}
            <SlideIn direction="left">
              <div className="bg-white rounded-2xl sm:rounded-3xl lg:rounded-[40px] border border-slate-200 p-5 sm:p-6 lg:p-8">
              <div className="flex flex-col items-center gap-6">
                {/* Avatar with edit button */}
                <div className="relative">
                  <ProfilePictureUploadGeneric
                    profileType="freelancer"
                    profileId={freelancerProfileId}
                    currentUrl={freelancerProfile?.profile_picture_url || null}
                    name={displayName || ''}
                  />
                </div>

                {/* Name and Headline */}
                <div className="text-center space-y-3 w-full">
                  {isEditingNameHeadline ? (
                    <div className="space-y-4 w-full">
                      <div className="space-y-1 text-left">
                        <Label htmlFor="displayName" className="text-sm">Name</Label>
                        <Input
                          id="displayName"
                          value={tempDisplayName}
                          onChange={(e) => setTempDisplayName(e.target.value)}
                          className="text-center text-[32px] font-normal tracking-tight h-auto rounded-xl border-2 border-slate-200 focus:border-[#4669ed] focus:ring-0"
                          placeholder="Your Name"
                        />
                      </div>
                      <div className="space-y-1 text-left">
                        <div className="flex items-center justify-between">
                          <Label htmlFor="headline" className="text-sm">Headline</Label>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => generateHeadline(tempHeadline)}
                            disabled={isAIGenerating}
                            className="h-7 px-2 text-xs text-[#4669ed] hover:text-[#2b42a3] hover:bg-[#4669ed]/10"
                          >
                            {isAIGenerating && generatingType === 'headline' ? (
                              <>
                                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                Generating...
                              </>
                            ) : (
                              <>
                                <Sparkles className="h-3 w-3 mr-1" />
                                AI Assist
                              </>
                            )}
                          </Button>
                        </div>
                        <Input
                          id="headline"
                          value={tempHeadline}
                          onChange={(e) => setTempHeadline(e.target.value)}
                          className="text-center text-xl text-slate-500 h-auto rounded-xl border-2 border-slate-200 focus:border-[#4669ed] focus:ring-0"
                          placeholder="AI/ML Specialist"
                        />
                      </div>
                      <div className="flex gap-2 pt-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setTempDisplayName(displayName);
                            setTempHeadline(headline);
                            setIsEditingNameHeadline(false);
                          }}
                          className="flex-1 rounded-full"
                        >
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          onClick={saveNameHeadline}
                          className="flex-1 bg-[#4669ed] hover:bg-[#2b42a3] rounded-full"
                        >
                          <Check className="h-4 w-4 mr-1" />
                          Save
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="relative inline-block">
                        <h1 className="text-2xl sm:text-[28px] lg:text-[32px] font-normal tracking-tight text-slate-900">
                          {displayName || 'Your Name'}
                        </h1>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setTempDisplayName(displayName);
                            setTempHeadline(headline);
                            setIsEditingNameHeadline(true);
                          }}
                          className="absolute -right-8 top-1/2 -translate-y-1/2 text-[#4669ed] hover:text-[#2b42a3] p-1 h-6 w-6"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                      </div>
                      <p className="text-xl text-slate-500">
                        {headline || 'AI/ML Specialist'}
                      </p>
                    </>
                  )}

                  {/* Review Status Badge */}
                  {reviewStatus && (
                    <div className="flex justify-center pt-2">
                      <Badge className={cn(
                        "rounded-full px-5 py-2 text-xs font-semibold tracking-wide uppercase shadow-sm border",
                        reviewStatus === 'approved' 
                          ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                        reviewStatus === 'pending' 
                          ? 'bg-amber-50 text-amber-700 border-amber-200' :
                        reviewStatus === 'changes_requested' 
                          ? 'bg-orange-50 text-orange-700 border-orange-200' :
                          'bg-slate-50 text-slate-700 border-slate-200'
                      )}>
                        {reviewStatus === 'approved' ? '✓ Approved' :
                         reviewStatus === 'pending' ? '⏳ Pending Review' :
                         reviewStatus === 'changes_requested' ? '✎ Changes Requested' :
                         'Draft'}
                      </Badge>
                    </div>
                  )}
                </div>
              </div>
              </div>
            </SlideIn>

            {/* Profile Completion - Only show if not complete */}
            {profileCompletion.percentage < 100 && (
              <div className="w-full bg-slate-100 rounded-2xl sm:rounded-[27px] p-4 sm:p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-slate-500 text-sm">Profile Completion</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex-1 bg-slate-200 rounded-md h-3 overflow-hidden">
                    <div
                      className="h-full bg-emerald-500 transition-all duration-500"
                      style={{ width: `${profileCompletion.percentage}%` }}
                    />
                  </div>
                  <span className="text-2xl font-normal text-slate-900">{profileCompletion.percentage}%</span>
                </div>

                {/* Completion Items */}
                <div className="space-y-2">
                  {profileCompletion.items.map((item, index) => (
                    <div
                      key={index}
                      className={cn(
                        "flex items-start gap-3 p-3 rounded-xl transition-colors",
                        item.completed
                          ? "bg-white/50"
                          : "cursor-pointer hover:bg-slate-200/50"
                      )}
                      onClick={() => {
                        if (!item.completed && item.route) {
                          navigate(item.route);
                        }
                      }}
                    >
                      {item.completed && (
                        <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 bg-emerald-500">
                          <Check className="h-4 w-4 text-white" />
                        </div>
                      )}
                      <div className="flex-1">
                        <p className={cn(
                          "text-sm font-semibold",
                          item.completed ? "text-emerald-700" : "text-slate-900"
                        )}>
                          {item.label}
                        </p>
                        <p className="text-xs text-slate-500">
                          {item.description}
                        </p>
                      </div>
                      {!item.completed && (
                        <ChevronRight className="h-5 w-5 text-slate-400" />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Availability Status Card */}
            <div className="bg-white rounded-xl sm:rounded-[20px] border border-slate-200 p-4 sm:p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <Clock className="h-5 w-5 text-slate-700" />
                  <h3 className="text-lg font-semibold text-slate-900">Availability</h3>
                </div>
                {!isEditingAvailability && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsEditingAvailability(true)}
                    className="text-[#4669ed]"
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                )}
              </div>

              {isEditingAvailability ? (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="availability" className="text-sm">Availability Status</Label>
                    <select
                      id="availability"
                      value={availabilityStatus}
                      onChange={(e) => setAvailabilityStatus(e.target.value)}
                      className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm ring-offset-white file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-slate-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4669ed] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <option value="">Select availability</option>
                      <option value="available">Available</option>
                      <option value="busy">Busy</option>
                      <option value="part-time">Part-time</option>
                      <option value="unavailable">Unavailable</option>
                    </select>
                  </div>
                  <div className="flex gap-2 pt-2">
                    <Button variant="outline" size="sm" onClick={() => setIsEditingAvailability(false)} className="flex-1 rounded-full">
                      Cancel
                    </Button>
                    <Button size="sm" onClick={saveAvailability} className="flex-1 bg-[#4669ed] hover:bg-[#2b42a3] rounded-full">
                      Save
                    </Button>
                  </div>
                </div>
              ) : (
                <div>
                  {availabilityStatus ? (
                    <Badge className={cn(
                      "rounded-full px-3 py-1.5 text-xs font-semibold",
                      availabilityStatus === 'available' ? 'bg-emerald-100 text-emerald-800' :
                      availabilityStatus === 'busy' ? 'bg-orange-100 text-orange-800' :
                      availabilityStatus === 'part-time' ? 'bg-blue-100 text-blue-800' :
                      'bg-slate-100 text-slate-800'
                    )}>
                      {availabilityStatus === 'available' ? 'Available' :
                       availabilityStatus === 'busy' ? 'Busy' :
                       availabilityStatus === 'part-time' ? 'Part-time' :
                       'Unavailable'}
                    </Badge>
                  ) : (
                    <p className="text-slate-400 text-sm">Click edit to set availability</p>
                  )}
                </div>
              )}
            </div>

            {/* Rate Range Card */}
            <div className="bg-white rounded-xl sm:rounded-[20px] border border-slate-200 p-4 sm:p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <DollarSign className="h-5 w-5 text-slate-700" />
                  <h3 className="text-lg font-semibold text-slate-900">Rate Range</h3>
                </div>
                {!isEditingRateRange && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsEditingRateRange(true)}
                    className="text-[#4669ed]"
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                )}
              </div>

              {isEditingRateRange ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="rateMin" className="text-sm">Min ($/hr)</Label>
                      <Input
                        id="rateMin"
                        type="number"
                        placeholder="50"
                        value={rateRangeMin}
                        onChange={(e) => setRateRangeMin(e.target.value)}
                        min="0"
                        step="1"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="rateMax" className="text-sm">Max ($/hr)</Label>
                      <Input
                        id="rateMax"
                        type="number"
                        placeholder="200"
                        value={rateRangeMax}
                        onChange={(e) => setRateRangeMax(e.target.value)}
                        min="0"
                        step="1"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2 pt-2">
                    <Button variant="outline" size="sm" onClick={() => setIsEditingRateRange(false)} className="flex-1 rounded-full">
                      Cancel
                    </Button>
                    <Button size="sm" onClick={saveRateRange} className="flex-1 bg-[#4669ed] hover:bg-[#2b42a3] rounded-full">
                      Save
                    </Button>
                  </div>
                </div>
              ) : (
                <div>
                  {rateRangeMin || rateRangeMax ? (
                    <p className="text-slate-900 font-medium">
                      {rateRangeMin && rateRangeMax ? `$${rateRangeMin}-${rateRangeMax}/hr` :
                       rateRangeMin ? `$${rateRangeMin}+/hr` :
                       `Up to $${rateRangeMax}/hr`}
                    </p>
                  ) : (
                    <p className="text-slate-400 text-sm">Click edit to set rate range</p>
                  )}
                </div>
              )}
            </div>

            {/* Professional Details Card - Sidebar */}
            <div className="bg-white rounded-xl sm:rounded-[20px] border border-slate-200 p-4 sm:p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <MapPin className="h-5 w-5 text-slate-700" />
                  <h3 className="text-lg font-semibold text-slate-900">Details</h3>
                </div>
                {!isEditingDetails && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsEditingDetails(true)}
                    className="text-[#4669ed]"
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                )}
              </div>

              {isEditingDetails ? (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="location" className="text-sm">Location</Label>
                    <Input
                      id="location"
                      placeholder="San Francisco, CA"
                      value={location}
                      onChange={(e) => setLocation(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="linkedin" className="text-sm">LinkedIn URL</Label>
                    <Input
                      id="linkedin"
                      placeholder="https://linkedin.com/in/..."
                      value={linkedinUrl}
                      onChange={(e) => setLinkedinUrl(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="website" className="text-sm">Website</Label>
                    <Input
                      id="website"
                      placeholder="https://yoursite.com"
                      value={websiteUrl}
                      onChange={(e) => setWebsiteUrl(e.target.value)}
                    />
                  </div>
                  <div className="flex gap-2 pt-2">
                    <Button variant="outline" size="sm" onClick={() => setIsEditingDetails(false)} className="flex-1 rounded-full">
                      Cancel
                    </Button>
                    <Button size="sm" onClick={saveDetails} className="flex-1 bg-[#4669ed] hover:bg-[#2b42a3] rounded-full">
                      Save
                    </Button>
                  </div>
                </div>
              ) : (
                (() => {
                  const profileWithViewCount = freelancerProfile as typeof freelancerProfile & { view_count?: number } | undefined;
                  return (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 text-sm">
                        <MapPin className="h-4 w-4 text-slate-400" />
                        <span className="text-slate-700">{location || 'Add location'}</span>
                      </div>
                      {linkedinUrl && (
                        <a href={linkedinUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-[#4669ed] hover:underline">
                          <Linkedin className="h-4 w-4" />
                          LinkedIn Profile
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                      {websiteUrl && (
                        <a href={websiteUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-[#4669ed] hover:underline">
                          <Globe className="h-4 w-4" />
                          Personal Website
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                      {/* View Count */}
                      {profileWithViewCount?.view_count !== undefined && (
                        <div className="flex items-center gap-2 text-sm text-slate-700">
                          <Eye className="h-4 w-4" />
                          <span>
                            {profileWithViewCount.view_count === 1
                              ? '1 view'
                              : `${profileWithViewCount.view_count || 0} views`}
                          </span>
                        </div>
                      )}
                      {!linkedinUrl && !websiteUrl && !location && (!freelancerProfile || profileWithViewCount?.view_count === undefined) && (
                        <p className="text-slate-400 text-sm">Click edit to add your details</p>
                      )}
                    </div>
                  );
                })()
              )}
            </div>

            {/* Languages Card */}
            <div className="bg-white rounded-xl sm:rounded-[20px] border border-slate-200 p-4 sm:p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <Globe className="h-5 w-5 text-slate-700" />
                  <h3 className="text-lg font-semibold text-slate-900">Languages</h3>
                </div>
                {!isEditingLanguages && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsEditingLanguages(true)}
                    className="text-[#4669ed]"
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                )}
              </div>

              {isEditingLanguages ? (
                <div className="space-y-4">
                  <div className="flex flex-wrap gap-2">
                    {SUPPORTED_LANGUAGES.map((language) => (
                      <Badge
                        key={language}
                        variant={languages.includes(language) ? 'default' : 'outline'}
                        className={cn(
                          "cursor-pointer transition-colors rounded-full px-3 py-1.5 text-sm",
                          languages.includes(language)
                            ? 'bg-[#d1e0fc] text-[#2b42a3] hover:bg-[#c1d4fb] border-transparent'
                            : 'hover:bg-slate-100 border-slate-200'
                        )}
                        onClick={() => {
                          if (languages.includes(language)) {
                            setLanguages(languages.filter(l => l !== language));
                          } else {
                            setLanguages([...languages, language]);
                          }
                        }}
                      >
                        {languages.includes(language) && <Check className="h-3 w-3 mr-1" />}
                        {language}
                      </Badge>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => setIsEditingLanguages(false)} className="flex-1 rounded-full">
                      Cancel
                    </Button>
                    <Button size="sm" onClick={saveLanguages} className="flex-1 bg-[#4669ed] hover:bg-[#2b42a3] rounded-full">
                      Save
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {languages.length > 0 ? languages.map((language) => (
                    <Badge
                      key={language}
                      className="bg-[#d1e0fc] text-[#2b42a3] rounded-full px-3 py-1.5 text-xs font-semibold"
                    >
                      {language}
                    </Badge>
                  )) : (
                    <p className="text-slate-400 text-sm">No languages added yet</p>
                  )}
                </div>
              )}
            </div>

            {/* Video Introduction Card */}
            <div className={cn(
              "rounded-2xl sm:rounded-3xl lg:rounded-[40px] p-4 sm:p-6",
              videoIntroUrls.length === 0
                ? "bg-amber-50/50 border-2 border-dashed border-amber-500/30"
                : "bg-white border border-slate-200 shadow-sm"
            )}>
              <div className="flex items-center justify-between mb-4 gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <Video className={cn(
                    "h-5 w-5 flex-shrink-0",
                    videoIntroUrls.length === 0 ? "text-amber-600" : "text-slate-700"
                  )} />
                  <h3 className="text-lg font-semibold text-slate-900 whitespace-nowrap">Video Introduction</h3>
                  {videoIntroUrls.length === 0 && (
                    <Badge className="bg-amber-500 text-white rounded-[48px] px-2 py-1 text-xs flex-shrink-0 whitespace-nowrap">
                      Recommended
                    </Badge>
                  )}
                </div>
                {videoIntroUrls.length > 0 && !isEditingVideoIntros && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsEditingVideoIntros(true)}
                    className="text-[#4669ed] flex-shrink-0"
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                )}
              </div>

              {isEditingVideoIntros ? (
                <div className="space-y-4">
                  {/* Existing videos */}
                  {videoIntroUrls.length > 0 && (
                    <div className="space-y-2">
                      <Label className="text-sm font-semibold">Your Videos</Label>
                      <div className="space-y-2">
                        {videoIntroUrls.map((url, index) => {
                          const videoInfo = getVideoEmbedInfo(url);
                          return (
                            <div key={index} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                              <div className="flex items-center gap-2 flex-1 min-w-0">
                                <Video className="h-4 w-4 text-slate-400 flex-shrink-0" />
                                <span className="text-sm text-slate-700 truncate">{url}</span>
                                {videoInfo.type && (
                                  <Badge variant="outline" className="text-xs flex-shrink-0">
                                    {videoInfo.type}
                                  </Badge>
                                )}
                              </div>
                              <button
                                type="button"
                                onClick={() => handleRemoveVideoUrl(url)}
                                className="ml-2 text-red-500 hover:text-red-700"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Add new video */}
                  <div className="space-y-2">
                    <Label htmlFor="videoUrl" className="text-sm">Add Video URL</Label>
                    <div className="flex gap-2">
                      <Input
                        id="videoUrl"
                        placeholder="https://youtube.com/watch?v=... or https://vimeo.com/... or https://loom.com/share/..."
                        value={newVideoUrl}
                        onChange={(e) => setNewVideoUrl(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddVideoUrl();
                          }
                        }}
                      />
                      <Button
                        variant="outline"
                        onClick={handleAddVideoUrl}
                        disabled={!newVideoUrl.trim()}
                        className="rounded-full"
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                    <p className="text-xs text-slate-500">Supports YouTube, Vimeo, and Loom URLs</p>
                  </div>

                  <div className="flex gap-2 pt-2">
                    <Button variant="outline" size="sm" onClick={() => setIsEditingVideoIntros(false)} className="flex-1 rounded-full">
                      Cancel
                    </Button>
                    <Button size="sm" onClick={saveVideoIntros} className="flex-1 bg-[#4669ed] hover:bg-[#2b42a3] rounded-full">
                      Save
                    </Button>
                  </div>
                </div>
              ) : videoIntroUrls.length === 0 ? (
                /* Empty state - matches UI static design */
                <div className="flex flex-col items-center text-center py-8">
                  <div className="w-20 h-20 rounded-[40px] bg-slate-200 flex items-center justify-center mb-4">
                    <Video className="h-6 w-6 text-slate-400" />
                  </div>
                  <h4 className="text-xl text-slate-900 mb-2">Record a video introduction</h4>
                  <p className="text-sm text-slate-500 max-w-[260px] mb-6">
                    Stand out by adding a 60-90 second video introducing yourself.
                    Profiles with videos get 3x more client views.
                  </p>
                  <Button
                    onClick={() => setIsEditingVideoIntros(true)}
                    className="bg-[#4669ed] hover:bg-[#2b42a3] rounded-[48px] px-6"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Video
                  </Button>
                </div>
              ) : (
                /* Video exists - show inline player */
                <div className="space-y-4">
                  {videoIntroUrls.map((url, index) => {
                    const videoInfo = getVideoEmbedInfo(url);
                    return (
                      <div key={index} className="space-y-2">
                        {videoInfo.embedUrl ? (
                          <div className="relative aspect-video bg-black rounded-2xl overflow-hidden">
                            <iframe
                              src={videoInfo.embedUrl}
                              className="w-full h-full"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                              allowFullScreen
                              title={`Video Introduction ${index + 1}`}
                            />
                          </div>
                        ) : (
                          <button
                            onClick={() => setSelectedVideoUrl(url)}
                            className="w-full flex items-center justify-between p-3 bg-slate-50 hover:bg-slate-100 rounded-lg transition-colors text-left"
                          >
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <Video className="h-4 w-4 text-slate-400 flex-shrink-0" />
                              <span className="text-sm text-slate-700 truncate">{url}</span>
                            </div>
                            <ChevronRight className="h-4 w-4 text-slate-400 flex-shrink-0" />
                          </button>
                        )}
                        {videoInfo.type && (
                          <div className="flex items-center gap-2 text-xs text-slate-500">
                            <Badge variant="outline" className="text-xs">
                              {videoInfo.type}
                            </Badge>
                            <span>Click fullscreen or use picture-in-picture</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Right Content */}
          <div className="flex-1 space-y-4 sm:space-y-6">

            {/* Incomplete Profile Banner */}
            {profileCompletion.percentage < 100 && (
              <SlideIn direction="up">
                <div
                  className="rounded-2xl sm:rounded-3xl lg:rounded-[40px] border-[3px] border-amber-500/60 p-5 sm:p-6 lg:p-8 text-center"
                  style={{ background: 'linear-gradient(99.87deg, rgba(245, 158, 11, 0.1) 0%, rgba(249, 115, 22, 0.1) 100%)' }}
                >
                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-white rounded-full mx-auto flex items-center justify-center mb-4">
                  <AlertCircle className="h-6 w-6 sm:h-8 sm:w-8 text-amber-500" />
                </div>
                <h2 className="text-xl sm:text-2xl lg:text-[32px] text-slate-900 tracking-tight mb-2">
                  Complete your profile to start getting matched
                </h2>
                <p className="text-base sm:text-lg text-slate-500 max-w-[600px] mx-auto">
                  Your profile is {profileCompletion.percentage}% complete. Add your experience and portfolio to increase your match rate by 3x and start receiving client opportunities.
                </p>
                </div>
              </SlideIn>
            )}

            {/* What I Do / Bio Section */}
            <div className="bg-white rounded-2xl sm:rounded-3xl lg:rounded-[40px] border border-slate-200 p-5 sm:p-6 lg:p-8">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 mb-4 sm:mb-6">
                <div className="flex items-center gap-3">
                  <Briefcase className="h-5 w-5 sm:h-6 sm:w-6 text-slate-700" />
                  <h3 className="text-xl sm:text-2xl text-slate-900">What I Do</h3>
                </div>
                {!isEditingBio && (
                  <button
                    onClick={() => { setTempBio(bio); setIsEditingBio(true); }}
                    className="flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3.5 rounded-[48px] border-2 border-slate-200 bg-white text-slate-900 font-normal text-sm sm:text-base hover:bg-slate-50 transition-colors"
                  >
                    <Pencil className="w-4 h-4 sm:w-5 sm:h-5" />
                    Edit
                  </button>
                )}
              </div>

              {isEditingBio ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="bio" className="text-sm text-slate-700">Bio</Label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setIsAIDialogOpen(true)}
                      disabled={isAIGenerating}
                      className="h-7 px-2 text-xs text-[#4669ed] hover:text-[#2b42a3] hover:bg-[#4669ed]/10"
                    >
                      {isAIGenerating && generatingType === 'bio' ? (
                        <>
                          <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <Sparkles className="h-3 w-3 mr-1" />
                          AI Assist
                        </>
                      )}
                    </Button>
                  </div>
                  <AIAssistantDialog
                    open={isAIDialogOpen}
                    onOpenChange={(open) => {
                      setIsAIDialogOpen(open);
                      if (!open) {
                        resetAIData();
                      }
                    }}
                    onGenerate={async (options) => {
                      return await generateBio(tempBio, options);
                    }}
                    onAccept={(text) => {
                      setTempBio(text);
                      resetAIData();
                    }}
                    generatedText={aiGeneratedData?.type === 'bio' ? aiGeneratedData.text : null}
                    isLoading={isAIGenerating && generatingType === 'bio'}
                    type="bio"
                    currentText={tempBio}
                  />
                  
                  <Textarea
                    id="bio"
                    value={tempBio}
                    onChange={(e) => setTempBio(e.target.value)}
                    className="min-h-[150px] resize-none"
                    placeholder="Describe what you do, your expertise, and what value you bring to clients..."
                  />
                  <div className="flex gap-2 justify-end">
                    <Button variant="outline" onClick={() => setIsEditingBio(false)} className="rounded-[48px]">
                      Cancel
                    </Button>
                    <Button onClick={saveBio} className="bg-[#4669ed] hover:bg-[#2b42a3] rounded-[48px]">
                      <Check className="h-5 w-5 mr-2" />
                      Save
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="text-slate-700 leading-relaxed whitespace-pre-wrap">
                  {bio || (
                    <p className="text-slate-400 italic">
                      Tell clients about your expertise, what problems you solve, and what makes you unique...
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Skills & Expertise Section */}
            <div className={cn(
              "rounded-2xl sm:rounded-3xl lg:rounded-[40px] p-5 sm:p-6 lg:p-8",
              skills.length === 0
                ? "bg-amber-50/50 border-2 border-dashed border-amber-500/30"
                : "bg-white border border-slate-200"
            )}>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 mb-4 sm:mb-6">
                <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                  <Code2 className="h-5 w-5 sm:h-6 sm:w-6 text-slate-700" />
                  <h3 className="text-xl sm:text-2xl text-slate-900">Skills & Expertise</h3>
                  {skills.length === 0 && (
                    <Badge className="bg-amber-500 text-white rounded-[48px] px-3 sm:px-4 py-1 text-xs">
                      Incomplete
                    </Badge>
                  )}
                </div>
                {skills.length > 0 && !isEditingSkills && (
                  <button
                    onClick={() => setIsEditingSkills(true)}
                    className="flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3.5 rounded-[48px] border-2 border-slate-200 bg-white text-slate-900 font-normal text-sm sm:text-base hover:bg-slate-50 transition-colors"
                  >
                    <Pencil className="w-4 h-4 sm:w-5 sm:h-5" />
                    Edit
                  </button>
                )}
              </div>

              {/* Always show skills editing interface when editing or no skills */}
              {(isEditingSkills || skills.length === 0) ? (
                <div className="space-y-6">
                  {/* Selected Skills */}
                  <div>
                    <Label className="text-sm font-bold text-slate-700 mb-3 block">
                      Your Skills ({skills.length}/10)
                    </Label>
                    <div className="flex flex-wrap gap-2 min-h-[40px]">
                      {skills.length > 0 ? skills.map((skill) => (
                        <Badge
                          key={skill}
                          className="bg-[#d1e0fc] text-[#2b42a3] rounded-[48px] px-3 py-1.5 text-xs font-semibold flex items-center gap-1"
                        >
                          {skill}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              removeSkill(skill);
                            }}
                            className="ml-1 hover:text-red-600"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      )) : (
                        <span className="text-slate-400 text-sm">No skills selected yet</span>
                      )}
                    </div>
                  </div>

                  {/* Suggested Skills */}
                  <div>
                    <Label className="text-sm font-bold text-slate-700 mb-3 block">Suggested Skills</Label>
                    {isLoadingSkills ? (
                      <div className="flex flex-wrap gap-2">
                        {[1, 2, 3, 4, 5].map((i) => (
                          <Skeleton key={i} className="h-8 w-24 rounded-full" />
                        ))}
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {suggestedSkills
                          .filter(skill => !skills.includes(skill.name))
                          .slice(0, 15)
                          .map((skill) => (
                            <Badge
                              key={skill.id}
                              variant="outline"
                              className="cursor-pointer hover:bg-[#4669ed]/10 hover:border-[#4669ed] rounded-full px-3 py-1.5 transition-all border-slate-200"
                              onClick={() => addSkill(skill.name)}
                            >
                              <Plus className="h-3 w-3 mr-1" />
                              {skill.name}
                            </Badge>
                          ))}
                      </div>
                    )}
                  </div>

                  {/* Custom Skill Input */}
                  <div className="flex gap-2">
                    <Input
                      placeholder="Add custom skill..."
                      value={customSkill}
                      onChange={(e) => setCustomSkill(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddCustomSkill();
                        }
                      }}
                      disabled={skills.length >= 10}
                    />
                    <Button
                      variant="outline"
                      onClick={handleAddCustomSkill}
                      disabled={skills.length >= 10 || !customSkill.trim()}
                      className="rounded-[48px]"
                    >
                      Add
                    </Button>
                  </div>

                  {/* Save/Cancel buttons */}
                  <div className="flex gap-2">
                    {isEditingSkills && (
                      <Button
                        variant="outline"
                        onClick={() => {
                          // Reset to original skills from profile
                          setSkills(Array.isArray(freelancerProfile?.skills) ? (freelancerProfile?.skills as string[]) : []);
                          setIsEditingSkills(false);
                        }}
                        className="rounded-[48px]"
                      >
                        Cancel
                      </Button>
                    )}
                    {skills.length > 0 && (
                      <Button onClick={saveSkills} className="bg-[#4669ed] hover:bg-[#2b42a3] rounded-[48px]">
                        <Check className="h-5 w-5 mr-2" />
                        Save Skills
                      </Button>
                    )}
                  </div>

                  {skills.length === 0 && !isEditingSkills && (
                    <div className="text-center py-4">
                      <p className="text-slate-500 text-sm max-w-[400px] mx-auto">
                        Help clients find you by adding your AI/ML skills, frameworks, and areas of expertise.
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                /* View mode - show skills as badges */
                <div className="flex flex-wrap gap-2">
                  {skills.map((skill) => (
                    <Badge
                      key={skill}
                      className="bg-[#d1e0fc] text-[#2b42a3] rounded-[48px] px-3 py-1.5 text-xs font-semibold"
                    >
                      {skill}
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            {/* Work Experience Section */}
            {freelancerProfileId && (
              <WorkExperienceSection
                freelancerProfileId={freelancerProfileId}
                isOwner={true}
              />
            )}

            {/* Portfolio Projects Section */}
            {freelancerProfileId && (
              <PortfolioSection
                freelancerProfileId={freelancerProfileId}
                isOwner={true}
              />
            )}

            {/* Training Progress Section */}
            <div className="bg-white rounded-2xl sm:rounded-3xl lg:rounded-[40px] border border-slate-200 p-5 sm:p-6 lg:p-8">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 mb-4 sm:mb-6">
                <div className="flex items-center gap-3">
                  <GraduationCap className="h-5 w-5 sm:h-6 sm:w-6 text-slate-700" />
                  <h3 className="text-xl sm:text-2xl text-slate-900">Training Progress</h3>
                </div>
                <Link to="/genius/courses">
                  <Button variant="outline" className="flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3.5 rounded-[48px] border-2 border-slate-200 bg-white text-slate-900 font-normal text-sm sm:text-base hover:bg-slate-50 transition-colors">
                    Continue Training
                  </Button>
                </Link>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                {courses?.slice(0, 2).map((course) => (
                  <Link key={course.id} to={`/genius/course/${course.slug}`}>
                    <div className="bg-white rounded-2xl sm:rounded-3xl lg:rounded-[40px] border border-slate-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                      <div
                        className="h-[140px] sm:h-[180px] flex items-center justify-center"
                        style={{ background: 'linear-gradient(180deg, rgba(232, 240, 254, 1) 0%, rgba(240, 245, 255, 1) 50%, rgba(232, 240, 254, 1) 100%)' }}
                      >
                        {course.thumbnail_url ? (
                          <img src={course.thumbnail_url} alt={course.title} className="w-full h-full object-cover" />
                        ) : (
                          <GraduationCap className="h-10 w-10 sm:h-12 sm:w-12 text-[#4669ed]" />
                        )}
                      </div>
                      <div className="p-4 sm:p-6">
                        <h4 className="text-lg font-bold text-slate-900 mb-2">{course.title}</h4>
                        <p className="text-slate-500 text-sm leading-relaxed mb-4 line-clamp-3">
                          {course.description}
                        </p>
                        <div className="flex items-center gap-6 text-slate-600 text-sm">
                          <div className="flex items-center gap-2">
                            <FolderOpen className="h-5 w-5" />
                            <span>{course.lessonsCount || 0} lessons</span>
                          </div>
                          {course.enrollment && (
                            <Badge className={cn(
                              "rounded-full px-2.5 py-1 text-[11px] font-bold uppercase",
                              course.enrollment.status === 'completed' ? "bg-emerald-100 text-emerald-800" :
                              "bg-blue-100 text-blue-800"
                            )}>
                              {course.enrollment.status === 'completed' ? 'Completed' : 'In Progress'}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            {/* Make Profile Searchable */}
            <div className="bg-slate-100/80 rounded-xl sm:rounded-[20px] p-4 sm:p-6">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <h4 className="font-medium text-slate-900">Make profile searchable</h4>
                  <p className="text-sm text-slate-500">Allow clients to find you in search results</p>
                </div>
                <Switch
                  checked={isSearchable}
                  onCheckedChange={(checked) => {
                    setIsSearchable(checked);
                    saveFreelancerProfile?.({ is_searchable: checked });
                  }}
                />
              </div>
            </div>

            {/* Submit for Review Section */}
            {freelancerProfile && (reviewStatus === 'draft' || reviewStatus === 'changes_requested') && (
              <div className={cn(
                "rounded-xl sm:rounded-[20px] p-4 sm:p-6 border",
                profileCompletion.percentage === 100
                  ? "bg-[#4669ed]/5 border-[#4669ed]/20"
                  : "bg-amber-50/50 border-amber-200/50"
              )}>
                {profileCompletion.percentage === 100 ? (
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                      <h4 className="font-medium text-slate-900">
                        {reviewStatus === 'changes_requested' ? 'Resubmit for Review' : 'Submit for Review'}
                      </h4>
                      <p className="text-sm text-slate-500">
                        {reviewStatus === 'changes_requested'
                          ? 'Make the requested changes and resubmit your profile'
                          : 'Submit your profile for admin review to make it visible to clients'}
                      </p>
                    </div>
                    <Button
                      onClick={() => submitForReview?.()}
                      disabled={isSubmittingForReview || !displayName.trim()}
                      className="bg-[#4669ed] hover:bg-[#2b42a3] rounded-[48px]"
                    >
                      {isSubmittingForReview ? 'Submitting...' : 'Submit for Review'}
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-4">
                    <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0" />
                    <div className="flex-1">
                      <h4 className="font-medium text-slate-900">
                        Complete Your Profile to Submit
                      </h4>
                      <p className="text-sm text-slate-500">
                        Your profile is {profileCompletion.percentage}% complete. Please complete all required sections (100%) before submitting for review.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}

          </div>
        </div>
      </div>

      {/* Video Player Dialog */}
      {selectedVideoUrl && (
        <VideoIntroPlayer
          url={selectedVideoUrl}
          isOpen={!!selectedVideoUrl}
          onClose={() => setSelectedVideoUrl(null)}
        />
      )}
    </div>
  );
}
